<script setup lang="ts">
import { ref, computed, onBeforeUnmount, nextTick, onMounted, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import PdfGenerator from '@/plugins/pdfGenerator'
import { useSignet } from '@/composables/useSignet'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import MobileSign from './MobileSign.vue'
import { DocumentStatus } from '@/types/DocumentStatus'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { umsokn, onLoaded, updateUmsokn } = useUmsokn()
const { createDocument, loadDocument, signInContract, getSigningStatus, signetDocument, spinner } = useSignet()
const umsoknType = inject("umsoknType", "");

// Detect co-signer mode via query params
const incomingDocId = computed(() => route.query.docId as string)
const incomingProjectId = computed(() => route.query.projectId as string)
const isCosigner = computed(() => route.query.cosigner === 'true')
const incomingSsn = computed(() => route.query.ssn as string)

// Unified document type: resolves from query param (co-signer link) or prop (application flow)
const documentType = computed(() => Number(route.query.type) || props.appId)

let pollTimer: number | null = null
const POLL_INTERVAL = 2000

// Error state for PDF fetch
const pdfFetchError = ref<string | null>(null)

// Props
const props = defineProps<{
  handlePrev?: () => void
  setLoading?: (val: boolean) => void
  showError?: (title: string, message: string) => void
  appId?: number
}>()

// Phone state
const phoneInfo = ref({
  phone: umsokn.value?.phone ?? '',
  phoneCode: '+354'
})

// Tabs
const deviceTab = ref<'simi' | 'app' | 'evrotrust'>('simi')
const device = computed<number>(() => {
  switch (deviceTab.value) {
    case 'simi': return 0 // AudkenniSIM
    case 'app': return 1 // AudkenniApp
    case 'evrotrust': return 3 // Evrotrust
    default: return 0
  }
})

// Validation
const rules = {
  phone: {
    required() {
      return device.value !== 1
        ? !!phoneInfo.value.phone && phoneInfo.value.phone.length > 0
        : true
    }
  },
  phoneCode: {
    required() {
      return device.value !== 1
        ? !!phoneInfo.value.phoneCode && phoneInfo.value.phoneCode.length > 0
        : true
    }
  },
  device: { required }
}
const v$ = useVuelidate(rules, {
  phone: computed(() => phoneInfo.value.phone),
  phoneCode: computed(() => phoneInfo.value.phoneCode),
  device
})

// Load or create document
async function initializeDocument() {
  props.setLoading?.(true)
  pdfFetchError.value = null
  try {
    if (incomingDocId.value) {
      // Co-signer: load existing document by docId
      await loadDocument(incomingDocId.value)
      if (signetDocument.value?.Document) {
        await nextTick()
        PdfGenerator.renderPdf(signetDocument.value.Document)
      } else {
        console.error('PDF fetch failed: No document returned for docId:', incomingDocId.value)
        pdfFetchError.value = t('undirritun_pdf_fetch_error') || 'Villa kom upp við að sækja skjal. Vinsamlegast reyndu aftur síðar.'
      }
    } else {
      // Original signer: create new document
      await createDocument({
        projectId: umsokn.value?.id,
        ssn: isCosigner.value ? incomingSsn.value : (umsokn.value?.ssn ?? null),
        phone: phoneInfo.value.phoneCode + phoneInfo.value.phone,
        docId: null,
        coSigners: umsokn.value?.coDeptors ?? null,
        type: documentType.value,
        lang: 0,
        device: device.value
      })

      if (signetDocument.value?.Document) {
        await nextTick()
        PdfGenerator.renderPdf(signetDocument.value.Document)
      } else {
        console.error('PDF fetch failed: No document returned after createDocument')
        pdfFetchError.value = t('undirritun_pdf_fetch_error') || 'Villa kom upp við að sækja skjal. Vinsamlegast reyndu aftur síðar.'
      }
    }
  } catch (err) {
    console.error('PDF fetch error:', err)
    pdfFetchError.value = t('undirritun_pdf_fetch_error') || 'Villa kom upp við að sækja skjal. Vinsamlegast reyndu aftur síðar.'
  } finally {
    props.setLoading?.(false)
  }
}

// For the normal application flow, onLoaded fires once umsokn is populated
onLoaded(initializeDocument)

onMounted(() => {
  document.addEventListener('visibilitychange', onVisibilityChange)

  // Co-signers arrive via the direct /undirritun route where umsokn is never
  // populated, so onLoaded will never fire. Kick off document loading immediately.
  if (isCosigner.value && incomingDocId.value) {
    initializeDocument()
  }
})

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    startPolling()
  }
}

onBeforeUnmount(() => {
  stopPolling()
  document.removeEventListener('visibilitychange', onVisibilityChange)
})

// Signing handler
async function handleSign() {
  // Guard: prevent signing if PDF fetch failed
  if (pdfFetchError.value) {
    return
  }

  const valid = await v$.value.$validate()
  if (!valid) return

  // ⬅️ stay loading while signing + polling
  props.setLoading?.(true)

  try {
    const result = await signInContract({
      projectId: incomingProjectId.value || umsokn.value?.id,
      ssn: umsokn.value?.ssn ?? null,
      phone: device.value === 1 ? null : (phoneInfo.value.phoneCode + phoneInfo.value.phone),
      docId: incomingDocId.value || signetDocument.value?.DocId,
      coSigners: umsokn.value?.coDeptors ?? null,
      coSigner: isCosigner.value,
      type: documentType.value,
      lang: 0,
      device: device.value,
      application: umsokn.value
    })

    if (result === false) {
      throw new Error('Signing failed')
    }

    // Start polling to check for status
    startPolling()
  } catch (err) {
    props.setLoading?.(false)
    props.showError?.(
      t('undirritun_error_title'),
      t('undirritun_error_message')
    )
  }
}

  function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

  async function handleSigningStatus(status: DocumentStatus) {
    switch (status) {
      case DocumentStatus.InSigning:
        // Co-deptors present: primary signer is done, redirect to "umsókn móttekin"
        if (umsokn.value?.coDeptors?.length) {
          stopPolling()
          try {
            await updateUmsokn(umsoknType)
            router.replace({ name: 'ApplicationSent' })
          } catch (err) {
            console.error('Failed to update application after signing:', err)
            props.setLoading?.(false)
            props.showError?.(
              t('undirritun_error_title'),
              t('undirritun_error_message') || 'Villa kom upp við að vista umsókn. Vinsamlegast reyndu aftur síðar.'
            )
          }
        }
        // No co-deptors: keep polling until fully signed
        break

      case DocumentStatus.Signed:
      case DocumentStatus.Reviewed:
        stopPolling()
        try {
          if (umsokn.value) {
            umsokn.value.signed = true
            await updateUmsokn(umsoknType)
          }
          router.replace({ name: 'ApplicationSent' })
        } catch (err) {
          console.error('Failed to update application after signing:', err)
          props.setLoading?.(false)
          props.showError?.(  
            t('undirritun_error_title'),
            t('undirritun_error_message') || 'Villa kom upp við að vista umsókn. Vinsamlegast reyndu aftur síðar.'
          )
        }
        break

      case DocumentStatus.Cancelled:
      case DocumentStatus.Rejected:
      case DocumentStatus.Deleted:
        stopPolling()
        props.setLoading?.(false)
        props.showError?.(
          t('undirritun_error_title'),
          t('undirritun_error_message')
        )
        break

      default:
        // New / InSigning / InReview → keep waiting
        break
    }
  }
    function startPolling() {
      stopPolling()

      const docId = signetDocument.value?.DocId || incomingDocId.value
      if (!docId) return

      pollTimer = window.setInterval(async () => {
        try {
          const status = await getSigningStatus(docId)
          await handleSigningStatus(status)
        } catch {
          // ignore transient errors
        }
      }, POLL_INTERVAL)
  }

</script>

<template>
  <div v-if="!spinner">
    <v-col cols="12" md="8" offset-md="2" xl="6" offset-xl="3">
      <h2 class="forms__header">{{ t('undirritun_umsokn_header1') }}</h2>
      <p>{{ t('undirritun_umsokn_header2') }}</p>

      <!-- Error Alert -->
      <v-alert
        v-if="pdfFetchError"
        type="error"
        variant="tonal"
        class="mb-4"
        closable
        @click:close="pdfFetchError = null"
      >
        {{ pdfFetchError }}
      </v-alert>

      <!-- Tabs -->
      <v-tabs v-model="deviceTab" class="mb-4">
        <v-tab value="simi">Sími</v-tab>
        <v-tab value="app">App</v-tab>
        <v-tab value="evrotrust">Evrotrust</v-tab>
      </v-tabs>

      <v-tabs-window v-model="deviceTab">
        <!-- Sími -->
        <v-tabs-window-item value="simi">
          <MobileSign
            :phone="phoneInfo.phone"
            :phoneCode="phoneInfo.phoneCode"
            :disable="false"
            :v$="v$"
            @updatePhone="val => (phoneInfo.phone = val)"
            @updateCode="val => (phoneInfo.phoneCode = val)"
          />
        </v-tabs-window-item>

        <!-- App -->
        <v-tabs-window-item value="app">
          <v-card flat class="pa-4">
            <v-text-field
              label="Kennitala"
              :model-value="isCosigner ? incomingSsn : umsokn?.ssn"
              disabled
              variant="outlined"
            />
            <p class="mt-2">{{ t('undirritun_umsokn_audkenni_text') }}</p>
          </v-card>
        </v-tabs-window-item>

        <!-- Evrotrust -->
        <v-tabs-window-item value="evrotrust">
          <MobileSign
            :phone="phoneInfo.phone"
            :phoneCode="phoneInfo.phoneCode"
            :disable="false"
            :v$="v$"
            @updatePhone="val => (phoneInfo.phone = val)"
            @updateCode="val => (phoneInfo.phoneCode = val)"
          />
          <p class="mt-2">{{ t('undirritun_umsokn_evrotrust_text') }}</p>
        </v-tabs-window-item>
      </v-tabs-window>

      <!-- PDF Preview -->

       <v-row>
      <div class="pdfButton__container">
        <div class="d-flex flex-row justify-space-between">
          <button class="forms__button" id="prev">Fyrri</button>
          <button class="forms__button" id="next">Næsta</button>
        </div>
      </div>
    </v-row>

      <div v-if="signetDocument?.Document" class="forms__center__contract mt-4">
        <canvas class="forms__contract" id="the-canvas"></canvas>
      </div>

      <div class="d-flex justify-center align-center page_num">
        Síða: <span id="page_num"></span> / <span id="page_count"></span>
      </div>
      <!-- Buttons -->
      <div class="forms__button__container">
        <button
          v-if="props.handlePrev"
          class="forms__button forms__button__back"
          @click="props.handlePrev()"
        >
          {{ t('lifeyri_umsokn_tilbaka') }}
        </button>
        <button
          class="forms__button forms__button__forward"
          :class="{ 'forms__button__disabled': !!pdfFetchError }"
          :disabled="!!pdfFetchError"
          @click="handleSign"
        >
          {{ t('lifeyri_umsokn_step7_undirritun') }}
        </button>
      </div>
    </v-col>

    <v-overlay :model-value="spinner" class="d-flex flex-column align-center justify-center">
      <v-progress-circular indeterminate size="64" color="primary" />
      <p>{{ t('undirritun_loading') }}</p>
    </v-overlay>
  </div>
</template>

<style scoped lang="scss">
.forms__center__contract {
  text-align: center;
}
.forms__contract {
  width: 100%;
  border: 1px solid #ddd;
}
.forms__button__container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
}
.forms__button {
  font-size: 16px;
  font-weight: bold;
  padding: 12px 24px;
  border: none;
  border-radius: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
}
.forms__button__back {
  background-color: #cccccc;
  color: #333;
}
.forms__button__forward {
  background: linear-gradient(180deg, #00bcb3 0%, #00877c 100%);
  color: #fff;
}
.forms__button__disabled {
  background: #cccccc;
  cursor: not-allowed;
  opacity: 0.6;
}


.pdfButton__container {
    width: 100%;
    height: 50px;
    margin: 2em 0;

    padding: 0 12px;

    .d-flex {
      width: 100%;
    }

    button {
      background-color: #cccccc;
      color: #333;
    }
}

</style>
